import { useTranslation } from 'react-i18next';
import { Card } from '../../components/core';
import { Icon, PencilIcon, TrashIcon } from '../../components/core';
import { type SeasonTime } from '../../services/seasonTime.service';
import { editBtnCls, deleteBtnCls } from './styles';
import { formatMonthDay } from '../../utils/formatters';

interface Props {
  seasonTimes: SeasonTime[];
  editingId: string | null;
  cropName: (_id: string) => string;
  regionName: (_id: string) => string;
  onEdit: (_st: SeasonTime) => void;
  onDelete: (_target: { id: string; label: string }) => void;
}

export function SeasonTimesTable({ seasonTimes, editingId, cropName, regionName, onEdit, onDelete }: Props) {
  const { t } = useTranslation();
  const thCls = 'px-4 py-2.5 text-start font-body text-xs font-semibold uppercase tracking-wide text-gray-500';

  return (
    <Card variant="info" className="hidden sm:block">
      <table className="w-full">
        <thead>
          <tr className="border-b border-green-100">
            <th className={thCls}>{t('seasonTimes.colCropType')}</th>
            <th className={thCls}>{t('seasonTimes.colRegion')}</th>
            <th className={thCls}>{t('seasonTimes.colStartDate')}</th>
            <th className={thCls}>{t('seasonTimes.colEndDate')}</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {seasonTimes.map(st => (
            <tr
              key={st.id}
              className={`border-b border-green-100 last:border-b-0 ${editingId === st.id ? 'bg-green-50' : ''}`}
            >
              <td className="px-4 py-3 font-body text-sm text-gray-900">{cropName(st.cropTypeId)}</td>
              <td className="px-4 py-3 font-body text-sm text-gray-900">{regionName(st.regionId)}</td>
              <td className="px-4 py-3 font-body text-sm text-gray-600">{formatMonthDay(st.startMonth, st.startDay, t)}</td>
              <td className="px-4 py-3 font-body text-sm text-gray-600">{formatMonthDay(st.endMonth, st.endDay, t)}</td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    aria-label={`${t('seasonTimes.editLabel')} ${cropName(st.cropTypeId)} ${regionName(st.regionId)}`}
                    onClick={() => onEdit(st)}
                    className={editBtnCls}
                  >
                    <Icon size="xs"><PencilIcon /></Icon>
                  </button>
                  <button
                    type="button"
                    aria-label={`${t('seasonTimes.deleteLabel')} ${cropName(st.cropTypeId)} ${regionName(st.regionId)}`}
                    onClick={() => onDelete({ id: st.id, label: `${cropName(st.cropTypeId)} × ${regionName(st.regionId)}` })}
                    className={deleteBtnCls}
                  >
                    <Icon size="xs"><TrashIcon /></Icon>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
